// src/encounter/lib/slash-trigger.ts
//
// Backslash insert (switch `slashInsert`). PURE: no React, no I/O.
// A physician types "\" and a trigger word ("\med amox", "\dx diab") to bring up ONE kind of item to add.
// This file decides which kind a typed text asks for, and nothing else. An unknown word is never a search.

export type SlashKind = 'medication' | 'diagnosis' | 'vaccination' | 'lab' | 'orders' | 'referral' | 'favourites' | 'suggestions' | 'sets';

export interface SlashKindEntry {
  kind: SlashKind;
  /** The kind's name as the hint grid and the "can't add" line say it. */
  label: string;
  /** Every word that asks for this kind (lower case, without the backslash). */
  triggers: readonly string[];
}

/**
 * The one table of triggers. The parser and the hint grid both read it. No two kinds share a trigger's first 3 letters
 * (#769 review: "problem" caught "\proc"), pinned by a test. "\lab" is lab orders only; "\ord" / "\order" are every
 * order kind this line adds (lab, imaging, procedure). "\ref" lists the clinic's specialities (PROD-3336) and is offered only with
 * the `referrals` switch on (`slashKindsFor`); off, "\ref" is an unknown word.
 */
export const SLASH_KINDS: readonly SlashKindEntry[] = [
  { kind: 'medication', label: 'Medication', triggers: ['med', 'medication', 'rx', 'drug', 'drugs'] },
  { kind: 'diagnosis', label: 'Diagnosis', triggers: ['dx', 'diag', 'diagnosis'] },
  { kind: 'vaccination', label: 'Vaccination', triggers: ['vac', 'vaccine', 'vax', 'imm', 'immunization'] },
  { kind: 'lab', label: 'Lab order', triggers: ['lab', 'labs'] },
  { kind: 'orders', label: 'Orders (lab, imaging, procedure)', triggers: ['ord', 'order', 'orders'] },
  { kind: 'referral', label: 'Referral', triggers: ['ref', 'refer', 'referral', 'referrals'] },
  { kind: 'favourites', label: 'Favourites', triggers: ['fav', 'favs', 'favourites', 'favorites'] },
];

/**
 * `dxOrderSplit` (Andrew, 4 Nov): the Add DIAGNOSIS line's own triggers. \fav your diagnosis favourites, \sug the AI diagnosis
 * suggestions, \set your order sets. No two share their first 3 letters.
 */
export const DX_SLASH_KINDS: readonly SlashKindEntry[] = [
  { kind: 'favourites', label: 'Your diagnosis favourites', triggers: ['fav', 'favs', 'favourites', 'favorites'] },
  { kind: 'suggestions', label: 'AI suggestions', triggers: ['sug', 'suggest', 'suggestions', 'ai'] },
  { kind: 'sets', label: 'Order sets', triggers: ['set', 'sets', 'orderset'] },
];

export type SlashQuery =
  /** A known kind, and what follows the word (the search text; may be empty). */
  | { kind: SlashKind; rest: string }
  /** Only the backslash so far. */
  | { kind: 'menu' }
  /** A word that is not a trigger. */
  | { kind: 'unknown'; word: string };

/** The kinds this user is offered: "\ref" only with the `referrals` switch on (its rows are the clinic's specialities). */
export function slashKindsFor({ referrals, ordersOnly = false }: { referrals: boolean; ordersOnly?: boolean }): readonly SlashKindEntry[] {
  // `dxOrderSplit`: the ORDER line offers no "\dx" (diagnoses are added on their own line).
  return SLASH_KINDS.filter((k) => (referrals || k.kind !== 'referral') && (!ordersOnly || k.kind !== 'diagnosis'));
}

/** The letters compared: the first 3 of a word of 3 or more letters. */
const PREFIX = 3;

function kindOfWord(word: string, kinds: readonly SlashKindEntry[]): SlashKind | undefined {
  const w = word.toLowerCase();
  if (w.length < PREFIX) {
    // A word of 1 or 2 letters matches only a trigger of the same length, exactly ("dx", "rx").
    return kinds.find((k) => k.triggers.some((t) => t.length < PREFIX && t === w))?.kind;
  }
  const head = w.slice(0, PREFIX);
  return kinds.find((k) => k.triggers.some((t) => t.length >= PREFIX && t.slice(0, PREFIX) === head))?.kind;
}

/** Reads a typed text. Returns null when it does not start with a backslash (not a slash text at all). `kinds` defaults to
 *  every kind; the line passes `slashKindsFor(...)`. */
export function parseSlashQuery(text: string, kinds: readonly SlashKindEntry[] = SLASH_KINDS): SlashQuery | null {
  if (!text.startsWith('\\')) return null;
  const body = text.slice(1);
  const match = /^(\S*)\s*([\s\S]*)$/.exec(body);
  const word = match?.[1] ?? '';
  if (!word) return { kind: 'menu' };
  const kind = kindOfWord(word, kinds);
  if (!kind) return { kind: 'unknown', word };
  return { kind, rest: (match?.[2] ?? '').trim() };
}

/** The triggers the hint grid shows for a kind: the short ones (4 letters or fewer), so a row stays one line. */
export function shownTriggers(entry: SlashKindEntry): string[] {
  return entry.triggers.filter((t) => t.length <= 4);
}
